import { z } from 'zod';
/**
 * The Data Sync Cockpit (spec §13.4): the platform sync, its field mapping,
 * the job monitor and the error queue.
 *
 * What the Algihaz platform's project feed looks like is not known yet
 * (OPEN_QUESTIONS Q22), so nothing here assumes its field names: the mapping
 * says which source field fills which QMS360 field, and it is data, versioned.
 */
/** Where the feed comes from. `sample` is a built-in feed for trying the sync out. */
export const projectSyncConfigSchema = z.object({
    source: z.enum(['sample', 'url']),
    url: z.string().trim().url().max(2048).nullable().optional(),
    /** Where the list of records sits in the response, as a dot path. Empty = the response is the list. */
    recordsPath: z.string().trim().max(200).nullable().optional(),
    /** What QMS360 records as the projects' origin. */
    externalSource: z.string().trim().min(1).max(60),
    /** Run by itself this often. Null or absent = only when run by hand. */
    scheduleIntervalMinutes: z.number().int().min(15).max(7 * 24 * 60).nullable().optional(),
}).refine((config) => config.source !== 'url' || Boolean(config.url), {
    message: 'Give the address of the feed.',
    path: ['url'],
});
/** The QMS360 fields a project feed can fill. Three are required. */
export const PROJECT_SYNC_FIELDS = [
    { key: 'externalId', label: 'Platform identifier', required: true },
    { key: 'code', label: 'Project code', required: true },
    { key: 'name', label: 'Project name', required: true },
    { key: 'nameAr', label: 'Project name (Arabic)', required: false },
    { key: 'businessUnitCode', label: 'Business unit code', required: false },
    { key: 'projectGroup', label: 'Project group', required: false },
];
/** The mapping QMS360 starts with, until an administrator saves one. */
export const DEFAULT_PROJECT_MAPPING = {
    externalId: 'id',
    code: 'code',
    name: 'name',
    nameAr: 'nameAr',
    businessUnitCode: 'businessUnitCode',
    projectGroup: 'group',
};
const sourceField = z.string().trim().max(120).regex(/^[A-Za-z0-9_.-]*$/, 'A source field is a name or a dot path.');
export const projectMappingSchema = z
    .object({
    externalId: sourceField.min(1),
    code: sourceField.min(1),
    name: sourceField.min(1),
    nameAr: sourceField.optional(),
    businessUnitCode: sourceField.optional(),
    projectGroup: sourceField.optional(),
})
    .strict();
export const saveMappingSchema = z.object({
    mapping: projectMappingSchema,
    note: z.string().trim().max(500).nullable().optional(),
});
/** One project as QMS360 will hold it, after the mapping. */
/** A required text field whose absence reads as a sentence, not a type error. */
const requiredText = (missing, max) => z.string({ required_error: missing, invalid_type_error: missing }).trim().min(1, missing).max(max);
export const syncedProjectSchema = z.object({
    externalId: requiredText('No platform identifier.', 200),
    code: requiredText('No project code.', 40),
    name: requiredText('No project name.', 200),
    nameAr: z.string().trim().max(200).nullable().optional(),
    businessUnitCode: z.string().trim().max(40).nullable().optional(),
    projectGroup: z.string().trim().max(100).nullable().optional(),
});
export const syncRunSchema = z.object({
    /** Report what would change, and change nothing. */
    dryRun: z.boolean().optional(),
});
export const syncErrorIgnoreSchema = z.object({
    reason: z.string().trim().min(5, 'Say why this record is being set aside.').max(1000),
});
export const syncJobsQuerySchema = z.object({
    jobKey: z.string().trim().max(60).optional(),
    before: z.string().uuid().optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
});
export const syncErrorsQuerySchema = z.object({
    status: z.enum(['open', 'fixed', 'ignored']).optional(),
});
/** Read a value at a dot path: `project.code` from `{ project: { code } }`. */
export function readPath(record, path) {
    if (!path)
        return record;
    let current = record;
    for (const part of path.split('.')) {
        if (current === null || typeof current !== 'object')
            return undefined;
        current = current[part];
    }
    return current;
}
//# sourceMappingURL=data-sync.js.map